import { Separator } from "@/components/ui/separator";
import { ContractAlertTargetResource, FlowPageInterface, PromotionResource } from "@/interfaces";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Button } from "./ui/button";
import { useContractAlertTargets } from "@/hooks/alerts/useContractAlertTargets";
import { useFlow } from "@/contexts/FlowContext";
import { formatCurrency, formatDate } from "@/utils/currency";
import { buildPriceLadder } from "@/utils/price-ladder";
import { localizedRebateTypeName } from "@/utils/rebate";
import { forfeitsPromotionDiscount, planFirstPaymentDate } from "@/utils/promotion-deadline";
import { paysInFullNow } from "@/utils/pay-in-full";
import { usePaymentSubscriptions } from "@/hooks/payments/usePaymentSubscriptions";
import { Download } from "lucide-react";
import { StatusChip, StatusChipVariant } from "./PaymentStatusChip";
import { useContract } from "@/hooks/useContract";
import { shouldShowAlertsStep } from "@/utils/contract-alerts";
import {ContactUs} from "@/components/ContactUs.tsx";
import { useInit } from "@follosoft/react-components";
import { useMemo } from "react";
import { formatPhone } from "@/utils/phone";

export const FlowPageResult = (props: FlowPageInterface) => {
  const { t, i18n } = useTranslation();
  const { contractId } = useFlow();
  const { constantsByGroup } = useInit();

  const invoiceStatuses = useMemo(() => {
    return constantsByGroup("STRIPE_INVOICE_STATUS");
  }, [constantsByGroup]);

  const paymentMethodTypes = useMemo(() => {
    return constantsByGroup("PAYMENT_METHOD_TYPES");
  }, [constantsByGroup]);

  const { 
    data: contract
  } = useContract(contractId);

  const services = contract?.relationships?.belongsToManyServices?.data;
  const address = contract?.relationships?.address?.data?.attributes;
  const company = contract?.relationships?.company?.data;

  const serviceBillingAddress = [
    address?.address_no,
    address?.address_street,
    address?.address_apt,
    address?.address_city,
    address?.address_province,
    address?.address_postalcode,
  ]
    .filter(Boolean)
    .join(", ");

  const { data: alertsData, isFetching: isAlertsFetching } =
    useContractAlertTargets(contractId);
  const hasAlerts = shouldShowAlertsStep(contract);

  const {
    data: paymentSubscriptions,
    isFetching: isPaymentSubscriptionsFetching,
  } = usePaymentSubscriptions(
    contractId,
    contract?.relationships?.subscriptionActive?.data?.id
  );

  const handleDownloadInvoice = (url: string) => {
    window.open(url, "_blank");
  };

  // Display prices
  const eligiblePromotion = props.selectedPaymentPlan?.relationships
      ?.eligiblePromotions?.data?.[0] as PromotionResource | undefined;

  // These are the promotions the plan *could* have earned. A customer who chose to
  // pay on the plan's own date declined a deadline-bearing one, so it must not
  // reappear on the confirmation page.
  //
  // Settling the whole balance today pays before every deadline, so a customer who
  // took that offer declined nothing.
  const paysInFull = paysInFullNow(props.selectedPaymentPlan, props.payInFull);
  const forfeitsDeadlineDiscount =
    !paysInFull &&
    forfeitsPromotionDiscount(
      eligiblePromotion,
      planFirstPaymentDate(props.selectedPaymentPlan),
      props.paymentTiming,
    );

  // Once the contract has been paid for there is a snapshot of what was actually
  // charged, and it is the only account of it that survives a reload — the timing
  // choice above lives in React state and comes back as "immediately". So the
  // snapshot decides, and the plan's list of promotions it *could* have earned is
  // ignored: a promotion the customer declined stays out of the eligibility filter
  // (no rebate was ever applied) and would otherwise reappear here on every reload.
  const paymentSnapshot =
    contract?.relationships?.subscriptionActive?.data?.attributes?.payment_snapshot ??
    contract?.meta?.payment_snapshot;
  const snapshotPromotion = paymentSnapshot?.appliedPromotion;

  const promoPrices = (paymentSnapshot || forfeitsDeadlineDiscount
    ? undefined
    : eligiblePromotion) as PromotionResource;

  const promoName = paymentSnapshot
    ? snapshotPromotion?.name
    : forfeitsDeadlineDiscount
      ? undefined
      : promoPrices?.attributes?.name;

  // promoPrices and the snapshot are mutually exclusive here — promoPrices is
  // undefined the moment a snapshot exists — so the ladder resolves to whichever
  // one the taxes and total below already came from.
  const priceLadder = buildPriceLadder({
    offer: promoPrices,
    snapshot: snapshotPromotion,
    prices: contract?.meta?.prices,
  });
  const displayTaxes = snapshotPromotion?.taxes ?? promoPrices?.attributes?.taxes ?? contract?.meta?.prices?.taxes;
  const displayTotal = snapshotPromotion?.newTotal ?? promoPrices?.attributes?.new_total ?? contract?.meta?.prices?.total ?? 0;
  const discounts = contract?.relationships?.rebates?.data;

  const noDataAlerts =
    (alertsData && alertsData.length === 0) || isAlertsFetching;
  const noDataPayments =
    !paymentSubscriptions?.relationships?.schedules?.data?.length &&
    !isPaymentSubscriptionsFetching;

  if (!props.currentlyVisible) {
    return <></>;
  }

  return (
    <div className="grid lg:grid-cols-3 grid-cols-1 gap-9">
      {/* PAYMENTS & ALERTS */}
      <div className="col-span-1 md:col-span-2 md:order-1 order-2">
        {paymentSubscriptions?.attributes?.status === "active" && (
          <>
            <h3 className="follo-label text-card-foreground">
              {t("fe.pages.flow.steps.results.title")}
            </h3>
            <div className="text-sm font-normal text-muted-foreground leading-5 my-3">
              <div className={"mb-2"}>
                {t("fe.pages.flow.steps.results.description")}
              </div>
            </div>
          </>
        )}
        <div className={"follo-card mt-7"}>
          <div
            className={
              "flex justify-between items-center border-zinc-200 border-b-1 px-7 py-5"
            }
          >
            <h3 className="follo-label">
              {t("fe.pages.flow.steps.results.tables.calendar.title")}
            </h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="px-6 min-w-[150px] text-muted-foreground font-medium text-sm">
                  {t("fe.pages.flow.steps.results.tables.calendar.date")}
                </TableHead>
                <TableHead className="px-6 min-w-[150px] text-muted-foreground font-medium text-sm">
                  {t("fe.pages.flow.steps.results.tables.calendar.amount")}
                </TableHead>
                <TableHead className="px-6 min-w-[150px] text-muted-foreground font-medium text-sm">
                  {t("fe.pages.flow.steps.results.tables.calendar.status")}
                </TableHead>
                <TableHead className="px-6 min-w-[150px] text-muted-foreground font-medium text-sm">
                  {t("fe.pages.flow.steps.results.tables.calendar.method")}
                </TableHead>
                <TableHead className="px-6 text-muted-foreground font-medium text-sm" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {noDataPayments ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {t("fe.pages.flow.steps.results.tables.emptyPaymentsState")}
                  </TableCell>
                </TableRow>
              ) : (
                paymentSubscriptions?.relationships?.schedules?.data?.map(
                  (item) => {
                    const currentStatus = invoiceStatuses.find(
                      (s) => s.id === String(item.attributes.invoice_status_id)
                    );
                    const currentPaymentMethod = paymentMethodTypes.find(
                      (p) => p.id === String(item.attributes.payment_method?.id)
                    );
                    return (
                      <TableRow key={item.id}>
                        <TableCell className="px-6 font-normal h-12 text-sm text-foreground">
                          {formatDate(item.attributes.date, i18n.language)}
                        </TableCell>
                        <TableCell className="px-6 font-normal h-12 text-sm text-foreground">
                          {formatCurrency(Number(item.attributes.amount), i18n.language)}
                        </TableCell>
                        <TableCell className="px-6 font-normal h-12 text-sm text-foreground">
                          <StatusChip
                            label={
                              currentStatus
                                ? t(`constants.stripeInvoiceStatus.${currentStatus.attributes.key.toLowerCase()}`)
                                : ""
                            }
                            variant={(currentStatus?.attributes.key.toLowerCase() ?? "default") as StatusChipVariant}
                          />
                        </TableCell>
                        <TableCell className="px-6 font-normal h-12 text-sm text-foreground">
                          {currentPaymentMethod
                            ? t(`constants.paymentMethodTypes.${currentPaymentMethod.attributes.key.toLowerCase().replace(/\s+/g, "_")}`)
                            : "—"}
                        </TableCell>
                        <TableCell className="px-6 font-normal h-12 text-sm text-foreground">
                          {item.attributes.is_paid && (
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() =>
                                handleDownloadInvoice(
                                  item.attributes.invoice_pdf_url ?  item.attributes.invoice_pdf_url + "&lang=" + i18n.language: ""
                                )
                              }
                              className="cursor-pointer"
                            >
                              <Download
                                size={18}
                                className="text-primary cursor-pointer"
                              />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  }
                )
              )}
            </TableBody>
          </Table>
        </div>
        {/* Alerts off for this contract means the tab that manages these contacts is gone,
            so listing them here would show contacts nobody can edit or delete and none of
            which will ever be called. */}
        {hasAlerts && (
          <div className={"follo-card mt-7"}>
            <div
              className={
                "flex justify-between items-center border-zinc-200 border-b-1 px-6 py-5"
              }
            >
              <h3 className="follo-label">
                {t("fe.pages.flow.steps.results.tables.contact.title")}
              </h3>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="px-6 min-w-[150px] text-muted-foreground text-sm">
                    {t(
                      "fe.pages.flow.steps.results.tables.contact.communication_method"
                    )}
                  </TableHead>
                  <TableHead className="px-6 min-w-[150px] text-muted-foreground font-medium text-sm">
                    {t("fe.pages.flow.steps.results.tables.contact.contact")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {noDataAlerts ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="h-24 text-center text-muted-foreground"
                    >
                      {t("fe.pages.flow.steps.alerts.table.emptyState")}
                    </TableCell>
                  </TableRow>
                ) : (
                  (alertsData ?? []).map(
                    (row: ContractAlertTargetResource, index: number) => {
                      return (
                        <TableRow key={index}>
                          <TableCell className="px-6 font-normal h-12 text-sm text-foreground gap-2">
                            {t(
                              `models.alertContractModel.alerts.options.${row.attributes.method.toLowerCase()}`
                            )}
                          </TableCell>
                          <TableCell className="px-6 font-normal h-12 text-sm text-foreground">
                            {row.attributes.method === "EMAIL"
                              ? row.attributes.target
                              : formatPhone(row.attributes.target)}
                          </TableCell>
                        </TableRow>
                      );
                    }
                  )
                )}
              </TableBody>
            </Table>
          </div>
        )}
        {/* HIDE FOR NOW */}
        {/* <div className={"flex justify-end mt-7"}>
          <Button className="cursor-pointer">
            {t("fe.pages.flow.steps.results.dashboard")}
          </Button>
        </div> */}
        <ContactUs email={company?.attributes.email} />
      </div>
      {/* SUMMARY */}
      <div className="col-span-1 md:order-2 order-1">
        <div className="follo-card px-8 py-7">
          <section>
            <h3 className="follo-label mb-4.5">
              {t("fe.pages.flow.steps.summary.title2")}
            </h3>
            <div
              className={
                "text-sm leading-5 font-medium text-card-foreground pb-3.5"
              }
            >
              {t("fe.pages.flow.steps.summary.details.addressTitle")}
            </div>
            <div
              className={
                "text-sm leading-5 font-normal text-card-foreground pb-6 max-w-2/3"
              }
            >
              {serviceBillingAddress}
            </div>
            <div
              className={
                "text-sm leading-5 font-medium text-card-foreground pb-3.5"
              }
            >
              {t("fe.pages.flow.steps.summary.details.servicesTitle")}
            </div>
            {services?.map((service, index) => {
              return (
                <div
                  key={index + "_" + service.id}
                  className={
                    "flex justify-between text-sm leading-5 font-normal text-card-foreground pb-3"
                  }
                >
                  <div className="max-w-2/3">{service.attributes.name}</div>
                  <div>{formatCurrency(service.meta.pivot.price ?? 0, i18n.language)}</div>
                </div>
              );
            })}
            <Separator className="mt-4 mb-6" />
            <div
              className={
                "flex justify-between text-sm leading-5 font-normal text-card-foreground pb-3"
              }
            >
              <div>{t("fe.pages.flow.steps.summary.details.subTotal")}</div>
              <div>
                {formatCurrency(priceLadder.subtotal ?? 0, i18n.language)}
              </div>
            </div>
            {discounts && discounts.length > 0 && (
              <>
                <div className={"text-sm leading-5 font-medium text-card-foreground pb-3.5"}>
                  {t("fe.pages.flow.steps.summary.details.discounts")}
                </div>
                {discounts.map((discount, index) => {
                  const discountName = localizedRebateTypeName(discount, i18n.language);

                  return (
                    <div
                      key={index + "_" + discount.id}
                      className={
                        "flex justify-between text-sm leading-5 font-normal text-card-foreground pb-3"
                      }
                    >
                      <div className="max-w-2/3">
                        <div>{discountName ?? discount.attributes.note ?? "-"}</div>
                        {discountName && discount.attributes.note && (
                          <div className="text-xs leading-4 text-muted-foreground">
                            {discount.attributes.note}
                          </div>
                        )}
                      </div>
                      <div>{formatCurrency(-(discount.attributes.price ?? 0), i18n.language)}</div>
                    </div>
                  );
                })}
              </>
            )}
            {promoName && (
              <>
                <div className="flex flex-col gap-1 pb-3 text-[#0A4DBD]">
                  <div className="flex justify-between gap-2 text-sm leading-5 font-normal">
                    <div>
                      {t("fe.pages.flow.steps.summary.details.promotion", {
                        defaultValue: "Promotion",
                      })}
                    </div>
                    <div>{formatCurrency(-priceLadder.discount, i18n.language)}</div>
                  </div>
                  <div className="text-xs leading-4 font-medium">{promoName}</div>
                </div>
              </>
            )}
            {priceLadder.isDiscounted && priceLadder.netSubtotal != null && (
              <div
                className={
                  "flex justify-between text-sm leading-5 font-normal text-card-foreground pb-3"
                }
              >
                <div>
                  {t("fe.pages.flow.steps.summary.details.netSubTotal", {
                    defaultValue: "Net subtotal",
                  })}
                </div>
                <div>{formatCurrency(priceLadder.netSubtotal, i18n.language)}</div>
              </div>
            )}
            {displayTaxes?.taxes?.map((tax, index) => {
              return (
                <div
                  key={index + "_" + tax.code}
                  className={
                    "flex justify-between text-sm leading-5 font-normal text-card-foreground pb-3"
                  }
                >
                  <div>{tax.label}</div>
                  <div>{formatCurrency(tax.amount ?? 0, i18n.language)}</div>
              </div>
              )
            })}
            <div
              className={
                "flex justify-between text-sm leading-5 font-bold text-primary pb-4"
              }
            >
              <div>{t("fe.pages.flow.steps.summary.details.total")}</div>
              <div className={"text-base leading-6 font-bold"}>
                {formatCurrency(displayTotal ?? 0, i18n.language)}
              </div>
            </div>
            <Separator className="mt-4 mb-6" />
            <div
              className={
                "flex justify-between text-sm leading-5 font-normal text-card-foreground pb-3"
              }
            >
              <div>{t("fe.pages.flow.steps.results.totalContract")}</div>
              <div>{formatCurrency(displayTotal, i18n.language)}</div>
            </div>
            <div
              className={
                "flex justify-between text-sm leading-5 text-destructive font-semibold"
              }
            >
              <div>{t("fe.pages.flow.steps.results.balanceLeft")}</div>
              <div>{formatCurrency(contract?.meta?.prices?.balance, i18n.language)}</div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
